const STAR_PATH =
  'M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.007z'

const MAX_STARS = 5

const SIZES = { sm: 'h-3.5 w-3.5', md: 'h-5 w-5', lg: 'h-7 w-7' } as const

type Size = keyof typeof SIZES

function Star({ filled, size }: { filled: boolean; size: Size }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={`${SIZES[size]} shrink-0 ${filled ? 'text-warning' : 'text-foreground/25'}`}
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path d={STAR_PATH} />
    </svg>
  )
}

interface Props {
  /** 1-5, or null for unrated. Fractional values are allowed when displaying an average. */
  value: number | null
  /**
   * Omit to render read-only. When present the control becomes five buttons, and
   * clicking the current value clears it back to null -- the same toggle-off the
   * attempts chips use, so a mis-tapped star can be undone without a separate
   * "clear" affordance.
   */
  onChange?: (value: number | null) => void
  /**
   * Accessible name for the control. Required: this package carries no locale
   * bundles, so every string is passed in already translated by the app.
   */
  label: string
  /** Accessible name for the nth star. Interactive mode only. */
  starLabel?: (stars: number) => string
  /** Print the numeric value beside the stars. */
  showValue?: boolean
  /** Already-translated caption, e.g. "18 ratings". */
  countLabel?: string
  size?: Size
  className?: string
}

/**
 * A 1-5 star control, read-only or interactive.
 *
 * HeroUI has no rating primitive, so this is a hand-rolled exception to the
 * HeroUI-first rule rather than a plain-HTML shortcut.
 *
 * Read-only renders as a single element with one accessible name ("4.2 out of
 * 5") instead of five separate images, so a screen reader announces the rating
 * once rather than reading five stars in a row.
 */
export function StarRating({
  value,
  onChange,
  label,
  starLabel,
  showValue = false,
  countLabel,
  size = 'md',
  className = '',
}: Props) {
  const interactive = typeof onChange === 'function'

  if (interactive) {
    return (
      <div className={`flex items-center gap-1 ${className}`} role="group" aria-label={label}>
        {Array.from({ length: MAX_STARS }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            type="button"
            // Tapping the active star clears the rating, so there is a way back
            // to "no opinion" without a separate control.
            onClick={() => onChange(value === n ? null : n)}
            aria-label={starLabel?.(n) ?? `${n}`}
            aria-pressed={value != null && n <= value}
            className="rounded-full p-1 transition-transform active:scale-90"
          >
            <Star filled={value != null && n <= value} size={size} />
          </button>
        ))}
      </div>
    )
  }

  // Nullish, not just null: a route payload from an older cache can arrive with
  // the field absent, and an unrated route must render as nothing rather than
  // throwing inside a list row.
  if (value == null) return null

  const formatted = value.toFixed(1)

  // Partial fill for a fractional average: a full-colour row clipped to the
  // percentage, laid over a hollow row.
  const pct = (Math.max(0, Math.min(MAX_STARS, value)) / MAX_STARS) * 100

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} role="img" aria-label={label}>
      <span className="relative inline-flex">
        <span className="inline-flex">
          {Array.from({ length: MAX_STARS }, (_, i) => (
            <Star key={i} filled={false} size={size} />
          ))}
        </span>
        <span
          className="absolute inset-y-0 left-0 inline-flex overflow-hidden"
          style={{ width: `${pct}%` }}
        >
          {Array.from({ length: MAX_STARS }, (_, i) => (
            <Star key={i} filled size={size} />
          ))}
        </span>
      </span>
      {showValue && <span className="tabular-nums text-sm">{formatted}</span>}
      {countLabel && <span className="text-sm text-foreground/50">{countLabel}</span>}
    </span>
  )
}
